import { InternalEventParams, MappingFn } from './types';
import { getAccessValueForTrackingTags } from './access';
import { EventName, ExternalGetTrackingTagsFn, PropositionSection, TrackingMeta } from '../types';
import { Proposition } from '@ecosystem/newron-client-collections';

interface TrackingCoreMetaParams {
  proposition: string;
  meta: TrackingMeta;
  subsection: string;
  pageName: string;
}

export const mapCoreMetaParams = ({
  proposition,
  meta,
  subsection,
  pageName,
}: TrackingCoreMetaParams): InternalEventParams => {
  // cell information
  // format is ['BE-DEC_GARDEN_PRES']
  const cells = meta.pageContext.cell || [];

  const splitCells = cells.map(c => {
    const parts = c.split('-');
    return { country: parts[0], itemGroup: parts[1] };
  });

  const cellsFormatted = splitCells.map(({ country, itemGroup }) => `${country}/${itemGroup}`);
  const countries = Array.from(new Set(splitCells.map(o => o.country)));
  const itemGroups = Array.from(new Set(splitCells.map(o => o.itemGroup)));
  const periodicity = meta.pageContext.timeFrame ? meta.pageContext.timeFrame[0].split(':')[0] : '';

  const section: PropositionSection = proposition === Proposition.DistributionPanel ? 'Supply Chain' : 'Market 2.0';

  return {
    section,
    subsection,
    pageName,
    cells: cellsFormatted,
    periodicity,
    itemGroup: itemGroups,
    locationId: countries,
    numberOfCells: cells.length.toString(),
    cellAggregation: cells.length > 1 ? 'multicell' : 'singlecell',
    locationIdAggregation: countries.length > 1 ? 'many' : 'single',
    itemGroupAggregation: itemGroups.length > 1 ? 'many' : 'single',
    proposition,
    ...meta.eventParams,
  };
};

/**
 * Maps internal market 2 tab names (ie. scorecard, time, segments) to their tracking equivalents
 *
 * @param tabName
 * @returns
 */
export const mapTabName = (tabName: string): string => {
  const lookup = {
    segments: 'Segmentation',
    time: 'Time Series',
    scorecard: 'Scorecard',
  };
  return lookup[tabName] ?? tabName;
};

/**
 * Splits a timeframe parameter value into its consistuent parts
 */
export const splitTimeframeValue = (paramValue: string | string[]) => {
  const [aggregation = '', sequenceInfo = '', aggregationMethod = ''] = (
    Array.isArray(paramValue) ? paramValue[0] : paramValue
  ).split(':');

  let aggregationValue: string = aggregation;
  let aggregationMethodValue: string = aggregationMethod;

  switch (aggregationMethod) {
    case 'Year':
    case 'MAT':
    case 'YTD':
      aggregationValue = 'Year';
      break;
    case 'Quarter':
    case 'RollingQuarter':
      aggregationValue = 'Quarter';
      aggregationMethodValue = 'Rolling Quarter';
      break;
  }

  return {
    aggregation: aggregationValue,
    aggregationMethod: aggregationMethodValue,
    sequenceInfo,
  };
};

export const addTimeAggregationValues = (params: InternalEventParams, timeFrameValue: string | string[]): void => {
  const split = splitTimeframeValue(timeFrameValue);
  params.timeAggregation = split.aggregation;
  params.timeAggregationMethod = split.aggregationMethod;
};

export const mapInternalEventParams = (
  eventName: string,
  meta: TrackingMeta,
  params: InternalEventParams
): InternalEventParams => {
  // chart interactions can use the page name as the chart name
  if (eventName === EventName.ChartInteractions && !params.chartName) {
    params.chartName = params.pageName;
  }
  if (eventName === EventName.PageElementViewed && !params.elementName) {
    params.elementName = params.pageName;
  }
  // data export events will provide empty strings to be substituted by pageName
  if (isDataExportEvent(eventName)) {
    substituteEmptyStrings(params);
  }

  if (eventName === EventName.DataExported && meta.pageContext.timeFrame) {
    addTimeAggregationValues(params, meta.pageContext.timeFrame);
  }
  if (eventName === EventName.PageElementViewed && params.focusPeriod && params.focusPeriodValues) {
    addTimeAggregationValues(params, params.focusPeriodValues);
    delete params.focusPeriodValues;
  }
  if (eventName === EventName.FilterApplied && params.optionChosenValues) {
    if (params.filterName === 'timeFrame') {
      addTimeAggregationValues(params, params.optionChosenValues);
    }
    delete params.optionChosenValues;
  }
  return params;
};

function isDataExportEvent(eventName: string): boolean {
  return [EventName.DataExported, EventName.DataExportInitiated].includes(eventName as EventName);
}

function substituteEmptyStrings(params: InternalEventParams): void {
  if (params.elementName === '') {
    params.elementName = params.pageName;
  }
  if (params.tableName === '') {
    params.tableName = params.pageName;
  }
}

export const getTrackingTagsFn = (
  subsection: string,
  pageName: string,
  isOverrideMeta: boolean = false,
  calcTrackingParams?: MappingFn
): ExternalGetTrackingTagsFn => {
  return (eventName: EventName | string, meta: TrackingMeta) => {
    const subsectionMeta = isOverrideMeta ? subsection : meta.pageTitle || subsection;

    const { pageName: trackingPageName, proposition } = calcTrackingParams(eventName, meta);

    const pageNameMeta = pageName ? `${subsectionMeta} ${mapTabName(pageName)}` : trackingPageName;

    const trackingCoreMeta = mapCoreMetaParams({
      proposition,
      meta,
      subsection: subsectionMeta,
      pageName: pageNameMeta,
    });

    const params = mapInternalEventParams(eventName, meta, trackingCoreMeta);

    const cellsWithDashes = params.cells.map(cell => cell.replace('/', '-'));
    const access = getAccessValueForTrackingTags(cellsWithDashes, params.periodicity);
    return {
      access,
      ...params,
    };
  };
};
